import React, { useEffect, useState } from "react";
import Link from "next/link";
import { useRouter } from "next/router";
import {
	PRODUCTS_NAV,
	isProductsNavLinkActive,
} from "./products-nav-data";

function linkHasActiveChild(link, path) {
	if (link.matchPrefix && (path === link.matchPrefix || path.startsWith(`${link.matchPrefix}/`))) {
		return true;
	}
	return (link.children || []).some((child) => isProductsNavLinkActive(path, child));
}

function sectionHasActiveLink(section, path) {
	return section.links.some(
		(link) => isProductsNavLinkActive(path, link) || linkHasActiveChild(link, path),
	);
}

function NavEntry({ link, path }) {
	const hasChildren = Array.isArray(link.children) && link.children.length > 0;
	const childActive = hasChildren && linkHasActiveChild(link, path);
	const [open, setOpen] = useState(childActive);

	useEffect(() => {
		if (childActive) setOpen(true);
	}, [childActive]);

	if (!hasChildren) {
		const active = isProductsNavLinkActive(path, link);
		return (
			<li>
				<Link
					href={link.href}
					className={`tu-products-nav__link${active ? " is-active" : ""}`}
					aria-current={active ? "page" : undefined}
					prefetch={false}
				>
					{link.label}
				</Link>
			</li>
		);
	}

	const panelId = `tu-products-nav-sub-${link.id}`;

	return (
		<li className={childActive ? "is-sub-active" : undefined}>
			<button
				type="button"
				className="tu-products-nav__subtoggle"
				aria-expanded={open}
				aria-controls={panelId}
				onClick={() => setOpen((current) => !current)}
			>
				<span className="tu-products-nav__toggle-label">{link.label}</span>
				<span className="tu-products-nav__chevron" aria-hidden="true" />
			</button>
			<div
				id={panelId}
				className={`tu-products-nav__subpanel${open ? " is-open" : ""}`}
				hidden={!open}
			>
				<ul className="tu-products-nav__sublinks">
					{link.children.map((child) => {
						const active = isProductsNavLinkActive(path, child);
						return (
							<li key={child.href}>
								<Link
									href={child.href}
									className={`tu-products-nav__link${active ? " is-active" : ""}`}
									aria-current={active ? "page" : undefined}
									prefetch={false}
								>
									{child.label}
								</Link>
							</li>
						);
					})}
				</ul>
			</div>
		</li>
	);
}

export default function ProductsNav({ locale = "en" }) {
	const router = useRouter();
	const nav = PRODUCTS_NAV[locale] || PRODUCTS_NAV.en;
	const path = router.asPath?.split("?")[0] || router.pathname;

	const [openSections, setOpenSections] = useState(() => new Set());

	useEffect(() => {
		setOpenSections((prev) => {
			const next = new Set(prev);
			for (const section of nav.sections) {
				if (sectionHasActiveLink(section, path)) {
					next.add(section.id);
				}
			}
			return next;
		});
	}, [path, nav.sections]);

	const toggleSection = (sectionId) => {
		setOpenSections((prev) => {
			const next = new Set(prev);
			if (next.has(sectionId)) {
				next.delete(sectionId);
			} else {
				next.add(sectionId);
			}
			return next;
		});
	};

	return (
		<nav className="tu-products-nav" aria-label={nav.title}>
			<div className="tu-products-nav__head">
				<p className="tu-products-nav__title">{nav.title}</p>
				<Link href={nav.allHref} className="tu-products-nav__all" prefetch={false}>
					{locale === "es" ? "Ver catálogo" : "View catalog"}
				</Link>
			</div>

			<ul className="tu-products-nav__sections">
				{nav.sections.map((section) => {
					const isOpen = openSections.has(section.id);
					const panelId = `tu-products-nav-${section.id}`;
					const sectionActive = sectionHasActiveLink(section, path);

					return (
						<li
							key={section.id}
							className={`tu-products-nav__section${
								sectionActive ? " is-section-active" : ""
							}`}
						>
							<button
								type="button"
								className="tu-products-nav__toggle"
								aria-expanded={isOpen}
								aria-controls={panelId}
								onClick={() => toggleSection(section.id)}
							>
								<span className="tu-products-nav__toggle-label">{section.label}</span>
								<span className="tu-products-nav__chevron" aria-hidden="true" />
							</button>

							<div
								id={panelId}
								className={`tu-products-nav__panel${
									isOpen ? " is-open" : ""
								}`}
								hidden={!isOpen}
							>
								{section.subtitle ? (
									<p className="tu-products-nav__subtitle">{section.subtitle}</p>
								) : null}
								<ul className="tu-products-nav__links">
									{section.links.map((link) => (
										<NavEntry
											key={link.id || link.href}
											link={link}
											path={path}
										/>
									))}
								</ul>
							</div>
						</li>
					);
				})}
			</ul>
		</nav>
	);
}
